import dayjs from 'dayjs';

import {
  ChecklistItem,
  DriverContext,
  EXPIRATION_WARNING_DAYS,
  StagedComplianceDoc,
} from '~hooks/useComplianceDocuments/checklist';
import { CompliantType } from '~hooks/useComplianceDocuments/models';
import {
  ComplianceDocumentType,
  ComplianceDocumentTypeBasic,
} from '~hooks/useComplianceDocumentTypes/models';

// Converts bare YYYY-MM-DD to YYYY-MM-DDTHH:mm:ssZ for the API's date-time
// format requirement. The Z suffix (UTC midnight) is safe because the read
// side uses dayjs.utc() to parse, preventing timezone-induced date shifts.
export function toDateTimeString(date: string | undefined): string | undefined {
  if (!date) return undefined;
  if (date.includes('T')) return date;
  return `${date}T00:00:00Z`;
}

export function computeStagedStatus(
  staged: StagedComplianceDoc,
  docType: ComplianceDocumentTypeBasic | ComplianceDocumentType,
): ChecklistItem['status'] {
  if (!docType.hasExpiration || !staged.expiresAt) {
    return 'valid';
  }
  const expiry = dayjs(staged.expiresAt);
  if (expiry.isBefore(dayjs())) {
    return 'expired';
  }
  if (expiry.isBefore(dayjs().add(EXPIRATION_WARNING_DAYS, 'day'))) {
    return 'expiring';
  }
  return 'valid';
}

export function isDocumentTypeRequiredForEntity(
  dt: ComplianceDocumentTypeBasic | ComplianceDocumentType,
  compliantType: CompliantType,
  driverContext?: DriverContext,
): boolean {
  if (compliantType === 'User') {
    if (driverContext === 'internal') return dt.requiredInternalDrivers;
    if (driverContext === 'shared') return dt.requiredSharedDrivers;
    return false;
  }
  if (compliantType === 'Account') return dt.requiredVendors;
  if (compliantType === 'Equipment') return dt.requiredEquipment;
  return false;
}

export function buildStagedChecklist(
  docTypes: ComplianceDocumentType[],
  compliantType: CompliantType,
  stagedDocs: StagedComplianceDoc[],
  driverContext?: DriverContext,
): ChecklistItem[] {
  const applicable = docTypes.filter((dt) =>
    isDocumentTypeRequiredForEntity(dt, compliantType, driverContext),
  );

  const stagedMap = new Map(stagedDocs.map((d) => [d.documentTypeId, d]));

  return applicable.map((dt) => {
    const staged = stagedMap.get(dt.id);
    if (staged) {
      return {
        documentType: dt,
        document: null,
        fileAttachment: null,
        stagedDoc: staged,
        status: computeStagedStatus(staged, dt),
      };
    }
    return {
      documentType: dt,
      document: null,
      fileAttachment: null,
      status: dt.dispatchGating ? 'missing' : 'optional',
    };
  });
}
